import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Sparkles, Eye, EyeOff } from "lucide-react";
import { PasswordRequirements } from "@/components/password-requirements";
import { isPasswordValid } from "@shared/password-policy";
import type { SafeUser } from "@shared/schema";

function pickFirstName(user: SafeUser | null): string {
  const first = user?.firstName?.trim();
  if (first) return first;
  const fromName = user?.name?.trim().split(/\s+/)[0];
  if (fromName) return fromName;
  return "there";
}

export function ForcePasswordChange() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const firstName = pickFirstName(user);

  const mutation = useMutation({
    mutationFn: async (newPassword: string) => {
      const res = await apiRequest("POST", "/api/auth/change-password", { newPassword });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/auth/me"], data);
      toast({
        title: "You're all set",
        description: "Your password is updated. Welcome aboard!",
      });
      setPassword("");
      setConfirm("");
    },
    onError: (err: any) => {
      toast({
        title: "We couldn't update your password",
        description: err.message || "Please try again in a moment.",
        variant: "destructive",
      });
    },
  });

  const passwordValid = isPasswordValid(password);
  const passwordsMatch = password === confirm;
  const canSubmit = passwordValid && passwordsMatch && confirm.length > 0 && !mutation.isPending;

  return (
    <Dialog open={true}>
      <DialogContent
        className={[
          "bg-brand-card-bg border-[#2a2a35] text-white",
          /* Remove the full-screen-height mobile default and center it */
          "h-auto top-1/2 -translate-y-1/2 translate-x-[-50%]",
          /* Comfortable width on all sizes */
          "w-[calc(100%-2rem)] max-w-md rounded-lg",
          /* Desktop sizing */
          "sm:w-full sm:max-w-md",
          /* Kill the close button — password must be set */
          "[&>button]:hidden",
        ].join(" ")}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2" data-testid="text-welcome-title">
            <Sparkles className="w-6 h-6 text-primary" />
            Welcome, {firstName} — let's set your password
          </DialogTitle>
          <DialogDescription className="text-agent-muted text-sm leading-relaxed">
            You're seeing this because an admin invited you and emailed a temporary password.
            Pick something only you'll know, and we'll get you into the portal.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate(password);
          }}
          className="space-y-4 mt-2"
        >
          <div>
            <Label htmlFor="new-password" className="text-xs text-agent-muted mb-1 block">New password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}

                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0d0d14] border-[#2a2a35] text-white pr-10"
                autoFocus
                autoComplete="new-password"
                data-testid="input-new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-agent-subtle hover:text-white transition-colors"
                aria-label={showNew ? "Hide password" : "Show password"}
                data-testid="button-toggle-new-password"
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <PasswordRequirements value={password} className="mt-2" testIdPrefix="new-password-rule" />
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-xs text-agent-muted mb-1 block">Confirm new password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}

                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-[#0d0d14] border-[#2a2a35] text-white pr-10"
                autoComplete="new-password"
                data-testid="input-confirm-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-agent-subtle hover:text-white transition-colors"
                aria-label={showConfirm ? "Hide password" : "Show password"}
                data-testid="button-toggle-confirm-password"
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirm.length > 0 && !passwordsMatch && (
              <p className="text-xs text-red-400 mt-1" data-testid="text-password-mismatch">Passwords don't match yet</p>
            )}
          </div>
          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-primary hover:bg-primary/90 text-brand-dark-bg font-semibold"
            data-testid="button-set-password"
          >
            {mutation.isPending ? "Saving..." : "Save password & continue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
